"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Autocomplete = void 0;
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
const React = tslib_1.__importStar(require("react"));
const classnames_1 = tslib_1.__importDefault(require("classnames"));
const fuse_js_1 = tslib_1.__importDefault(require("fuse.js"));
const input_1 = require("@nbc-design/input");
const progress_spinner_1 = require("@nbc-design/progress-spinner");
require("./autocomplete.css");
const item_1 = require("./item");
/** The autocomplete component is an input field that provides selectable suggestions as a user types into it. It allows users to quickly search through and select from large collections of options. */
exports.Autocomplete = React.forwardRef((props, ref) => {
    const { id, name, suggestions, staticSuggestions = [], className, position = 'auto', divProps, dataTest = 'autocomplete', isLoading = false, isExactSearch = true, onChange, onSelect, onFocus, preventSelectOnTab = false, selectRequired = false, displayListWithoutValue, value = '', limit, fuzzySearchOptions, noResultsText, autoComplete = 'off', length, reopenWithAllSuggestions = false, ...other } = props;
    const [suggestionsVisible, showSuggestions] = React.useState(false);
    const [userInput, setUserInput] = React.useState(value);
    const [activeSuggestion, setActiveSuggestion] = React.useState(-1);
    const [filteredSuggestions, setFilteredSuggestions] = React.useState([]);
    const [autocompletePosition, setPosition] = React.useState(position);
    const classes = (0, classnames_1.default)('dsc-autocomplete', suggestionsVisible && !isLoading && 'is-suggestionsVisible', isLoading && 'is-loading', autocompletePosition === 'top' && 'on-top', displayListWithoutValue && 'is-select', length && `is-${length}`, className);
    const suggestionsRef = React.useRef(null);
    const divRef = React.useRef(null);
    const reopenShowAllRef = React.useRef(false);
    const lastSelectedSuggestionRef = React.useRef(null);
    const groupBy = (collection, property) => {
        let i = 0;
        let val;
        let index;
        const values = [];
        const result = [];
        for (; i < collection.length; i++) {
            val =
                collection[i].category !== undefined
                    ? collection[i].category?.label
                    : collection[i][property];
            index = values.indexOf(val);
            if (index > -1)
                result[index].push(collection[i]);
            else {
                values.push(val);
                result.push([collection[i]]);
            }
        }
        return result;
    };
    const closeSuggestions = () => {
        setActiveSuggestion(-1);
        showSuggestions(false);
    };
    const handleOutsideClickAndTab = React.useCallback(() => {
        // outside click
        if (divRef?.current) {
            const input = divRef.current.getElementsByClassName('dsc-input');
            const inputValue = input.length > 0 ? input[0].value : '';
            const matchValueArray = suggestions.filter((suggestion) => suggestion.label === inputValue);
            const resetInputAndCloseSuggestion = () => {
                setUserInput('');
                closeSuggestions();
            };
            // if we force the user to select a suggestion
            // and the value in the field is not a suggestion
            // we reset the input value and close suggestion
            // otherwise, we just close the suggestions
            selectRequired && matchValueArray.length === 0 && inputValue
                ? resetInputAndCloseSuggestion()
                : closeSuggestions();
        }
    }, [selectRequired, suggestions]);
    const filterAndDisplaySuggestions = React.useCallback((currentUserInput, showAll = false) => {
        let currentFilteredSuggestions;
        if (showAll) {
            currentFilteredSuggestions = suggestions;
        }
        else if (isExactSearch) {
            currentFilteredSuggestions = suggestions.filter((suggestion) => {
                const textToSearch = suggestion.description
                    ? `${suggestion.label} ${suggestion.description}`
                    : suggestion.label;
                return textToSearch.toLowerCase().includes(currentUserInput.toLowerCase());
            });
        }
        else {
            currentFilteredSuggestions = suggestions;
            const options = {
                keys: ['label'],
                shouldSort: true,
                threshold: 0.3,
                ...fuzzySearchOptions,
            };
            const fuse = new fuse_js_1.default(suggestions, options);
            const results = displayListWithoutValue && !currentUserInput
                ? suggestions
                : fuse.search(currentUserInput);
            currentFilteredSuggestions = results;
        }
        // set positioning
        if (divRef?.current && position === 'auto') {
            const listMaxHeight = 256;
            const fieldRect = divRef.current.getBoundingClientRect();
            const fieldHeight = fieldRect.height;
            const maxBottomAutoComplete = fieldRect.top + fieldHeight + listMaxHeight;
            if (maxBottomAutoComplete > window.innerHeight && fieldRect.top - listMaxHeight > 0) {
                setPosition('top');
            }
            else {
                setPosition('bottom');
            }
        }
        else {
            setPosition(position);
        }
        if (showAll ||
            (displayListWithoutValue && !currentUserInput) ||
            (currentFilteredSuggestions.length > 0 && currentUserInput) ||
            (noResultsText && currentUserInput && document.activeElement)) {
            const input = divRef?.current?.getElementsByClassName('dsc-input');
            if (input?.[0] && document.activeElement === input[0]) {
                showSuggestions(true);
            }
        }
        else {
            showSuggestions(false);
        }
        if (limit) {
            currentFilteredSuggestions = currentFilteredSuggestions.slice(0, limit);
        }
        setUserInput(currentUserInput);
        setFilteredSuggestions(currentFilteredSuggestions);
        setActiveSuggestion(-1);
    }, [
        displayListWithoutValue,
        fuzzySearchOptions,
        isExactSearch,
        limit,
        noResultsText,
        position,
        suggestions,
    ]);
    React.useEffect(() => {
        if (divRef?.current) {
            const input = divRef.current.getElementsByClassName('dsc-input');
            const inputValue = input.length > 0 ? input[0].value : '';
            filterAndDisplaySuggestions(inputValue);
        }
        const handleClick = (e) => {
            if (divRef?.current?.contains(e.target)) {
                // inside click
                return;
            }
            handleOutsideClickAndTab();
        };
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, [handleOutsideClickAndTab, filterAndDisplaySuggestions, suggestions]);
    React.useEffect(() => {
        setUserInput(value);
    }, [value]);
    const onInputChange = (e) => {
        const currentUserInput = e.currentTarget.value;
        reopenShowAllRef.current = false;
        onChange?.(currentUserInput);
        filterAndDisplaySuggestions(currentUserInput);
    };
    const onInputFocus = (e) => {
        onFocus?.(e);
        if (reopenWithAllSuggestions && reopenShowAllRef.current) {
            filterAndDisplaySuggestions(e.target.value, true);
            if (lastSelectedSuggestionRef.current) {
                // set active suggestion to the selected one when reopening with all suggestions //
                const selectedIndex = suggestions.findIndex((s) => s.value === lastSelectedSuggestionRef.current.value);
                if (selectedIndex !== -1) {
                    setActiveSuggestion(selectedIndex);
                }
            }
        }
        else {
            filterAndDisplaySuggestions(e.target.value);
        }
    };
    const selectSuggestion = (suggestion) => {
        setUserInput(suggestion.label);
        onSelect?.(suggestion);
        if (reopenWithAllSuggestions) {
            reopenShowAllRef.current = true;
            lastSelectedSuggestionRef.current = suggestion;
        }
        closeSuggestions();
    };
    const scrollToActiveSuggestion = (refUl) => {
        const ul = refUl.current;
        const ulHeight = 256;
        const activeLi = ul?.getElementsByClassName('is-active')[0];
        const groupLabelHeight = ul?.getElementsByClassName('dsc-autocomplete__groupTitle').length > 0
            ? ul?.getElementsByClassName('dsc-autocomplete__groupTitle')[0].offsetHeight
            : 0;
        if (refUl && activeLi) {
            const viewport = ul.scrollTop - activeLi.offsetTop - activeLi.offsetHeight;
            if (viewport >= 0 - activeLi.offsetHeight - groupLabelHeight) {
                ul.scrollTo(0, activeLi.offsetTop - groupLabelHeight);
            }
            if (viewport <= 0 - ulHeight) {
                ul.scrollTo(0, activeLi.offsetTop - ulHeight + activeLi.offsetHeight);
            }
        }
    };
    React.useEffect(() => {
        scrollToActiveSuggestion(suggestionsRef);
    }, [activeSuggestion]);
    const onKeyDown = (e) => {
        let newIndex;
        switch (e.key) {
            case 'Enter':
                e.preventDefault();
                e.stopPropagation();
                if (activeSuggestion === -1) {
                    break;
                }
                const suggestionList = activeSuggestion >= filteredSuggestions.length ? staticSuggestions : filteredSuggestions;
                const selectedValue = suggestionsRef?.current
                    ?.getElementsByClassName('is-active')[0]
                    .getAttribute('data-value');
                const selectedSuggestion = suggestionList.filter((suggestion) => suggestion.value === selectedValue)[0];
                selectedSuggestion && selectSuggestion(selectedSuggestion);
                break;
            case 'Tab':
                if (activeSuggestion !== -1 && !preventSelectOnTab) {
                    selectSuggestion(filteredSuggestions[activeSuggestion]);
                }
                else {
                    handleOutsideClickAndTab();
                }
                break;
            case 'ArrowUp':
                newIndex =
                    activeSuggestion === -1
                        ? filteredSuggestions.length + (staticSuggestions?.length || 0) - 1
                        : activeSuggestion - 1;
                setActiveSuggestion(newIndex);
                break;
            case 'ArrowDown':
                newIndex =
                    activeSuggestion + 1 === filteredSuggestions.length + (staticSuggestions?.length || 0)
                        ? -1
                        : activeSuggestion + 1;
                setActiveSuggestion(newIndex);
                break;
            case 'Escape':
                closeSuggestions();
                break;
            default:
                break;
        }
    };
    const displayItem = (key, liClassName, index, suggestion, isActive, isStatic = false) => {
        return ((0, jsx_runtime_1.jsx)(item_1.Item, { keyValue: key, className: liClassName, itemIndex: index, suggestion: suggestion, userInput: userInput, selectSuggestion: selectSuggestion, setActiveSuggestion: setActiveSuggestion, isActive: isActive, isStatic: isStatic }, key));
    };
    const displayStaticSuggestions = () => {
        return staticSuggestions.map((suggestion, index) => {
            const indexTotal = index + filteredSuggestions.length;
            const isActive = index === activeSuggestion - indexTotal;
            const liClassName = (0, classnames_1.default)('dsc-autocomplete__item', isActive && 'is-active');
            const key = `${id}__staticItem${index}`;
            return displayItem(key, liClassName, indexTotal, suggestion, isActive, true);
        });
    };
    // SUGGESTIONS LIST
    let suggestionsListMarkup;
    if (suggestionsVisible && !isLoading) {
        if (filteredSuggestions.length > 0) {
            if (filteredSuggestions.some((filteredSuggestion) => filteredSuggestion.category)) {
                const grouped = groupBy(filteredSuggestions, 'label');
                let globalIndex = -1;
                suggestionsListMarkup = ((0, jsx_runtime_1.jsx)("ul", { className: "dsc-autocomplete__suggestions", role: "listbox", id: `${id}__suggestions`, ref: suggestionsRef, children: grouped.map((group, index) => {
                        const groupListClass = (0, classnames_1.default)('dsc-autocomplete__groupList', group[0].category && 'has-category', group[0].category?.icon && 'has-icon');
                        const groupKey = group[0].category && `${group[0].category?.label}_${index}`;
                        return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [group[0].category && ((0, jsx_runtime_1.jsxs)("div", { className: "dsc-autocomplete__groupTitle", children: [group[0].category?.icon && ((0, jsx_runtime_1.jsx)("div", { className: "dsc-autocomplete__icon", children: group[0].category?.icon })), (0, jsx_runtime_1.jsx)("div", { className: "dsc-autocomplete__label", children: group[0].category?.label })] }, groupKey)), (0, jsx_runtime_1.jsx)("ul", { className: groupListClass, 
                                    // to avoid crash when using automatic translation in chrome
                                    translate: "no", children: group.map((suggestion) => {
                                        globalIndex += 1;
                                        const isActive = globalIndex === activeSuggestion;
                                        const liClassName = (0, classnames_1.default)('dsc-autocomplete__item', isActive && 'is-active');
                                        const key = `${id}__item${globalIndex}`;
                                        return displayItem(key, liClassName, globalIndex, suggestion, isActive);
                                    }) })] }));
                    }) }));
            }
            else {
                suggestionsListMarkup = ((0, jsx_runtime_1.jsxs)("ul", { className: "dsc-autocomplete__suggestions", role: "listbox", id: `${id}__suggestions`, ref: suggestionsRef, 
                    // to avoid crash when using automatic translation in chrome
                    translate: "no", children: [filteredSuggestions.map((suggestion, index) => {
                            const isActive = index === activeSuggestion;
                            const liClassName = (0, classnames_1.default)('dsc-autocomplete__item', isActive && 'is-active');
                            const key = `${id}__item${index}`;
                            return displayItem(key, liClassName, index, suggestion, isActive);
                        }), staticSuggestions && displayStaticSuggestions()] }));
            }
        }
        else {
            const noResultsMarkup = noResultsText && ((0, jsx_runtime_1.jsx)("span", { className: "dsc-autocomplete__noResults", children: noResultsText }));
            suggestionsListMarkup = ((0, jsx_runtime_1.jsxs)("div", { className: "dsc-autocomplete__suggestions", children: [noResultsMarkup, staticSuggestions && ((0, jsx_runtime_1.jsx)("ul", { className: "dsc-autocomplete__noResultsStaticList", role: "listbox", id: `${id}__suggestions`, ref: suggestionsRef, children: displayStaticSuggestions() }))] }));
        }
    }
    const progressSpinnerMarkup = isLoading && (0, jsx_runtime_1.jsx)(progress_spinner_1.ProgressSpinner, { size: "small" });
    return ((0, jsx_runtime_1.jsxs)("div", { ...divProps, 
        // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
        role: "combobox", className: classes, "data-test": dataTest || name, "aria-owns": id, "aria-haspopup": "listbox", "aria-expanded": !!suggestionsVisible, ref: divRef, children: [(0, jsx_runtime_1.jsx)(input_1.Input, { ...other, id: id, name: name, onChange: onInputChange, onFocus: onInputFocus, onKeyDown: onKeyDown, dataTest: "input", value: userInput, autoComplete: autoComplete, "aria-autocomplete": "list", ...(activeSuggestion !== -1
                    ? {
                        'aria-activedescendant': `${id}__item${activeSuggestion}`,
                        'aria-controls': `${id}__suggestions`,
                    }
                    : {}), ref: ref }), progressSpinnerMarkup, suggestionsListMarkup] }));
});
exports.Autocomplete.displayName = 'Autocomplete';
//# sourceMappingURL=autocomplete.js.map